import useAuthUser from "@/composables/useAuthUser";
import { computed, type MaybeRef, toRef } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { accountsQuery, businessAccountsQuery } from "@/api/accountApi";
import { useFragment } from "@/api/generated";
import { AccountFragmentFragmentDoc } from "@/api/generated/graphql";
import type { WatchQueryFetchPolicy } from "@apollo/client/core/watchQueryOptions";
import { sortAccounts } from "@/utils/accounts";
import { group, isEmpty, sum } from "radash";
import { nonCashAccountTypes } from "@/features/FinancialOverview/constants.ts";
import { useBankingContextStore } from "@/stores/bankingContext";
import { storeToRefs } from "pinia";
import { BankingContextType } from "@/types/bankingContext";

export function useAccounts(
  options: { includeExternal?: MaybeRef<boolean>; fetchPolicy?: WatchQueryFetchPolicy; notifyOnNetworkStatusChange?: boolean } = {},
) {
  const includeExternal = toRef(options.includeExternal ?? true);
  const fetchPolicy = toRef(options.fetchPolicy ?? "cache-and-network");
  const notifyOnNetworkStatusChange = toRef(options.notifyOnNetworkStatusChange ?? false);

  const { authUser } = useAuthUser();
  const isUserLoggedIn = computed(() => !!authUser.value);

  const bankingContextStore = useBankingContextStore();
  const { context } = storeToRefs(bankingContextStore);
  const isPersonalContext = computed(() => context.value.type === BankingContextType.Personal);
  const businessId = computed(() => (context.value.type === BankingContextType.Business ? context.value.entityId : ""));

  const personalQueryOptions = computed(() => ({
    enabled: isUserLoggedIn.value && isPersonalContext.value,
    fetchPolicy: fetchPolicy.value,
    notifyOnNetworkStatusChange: notifyOnNetworkStatusChange.value,
  }));
  const personalQueryVariables = computed(() => ({
    includeExternal: includeExternal.value,
    includeBusinessAccounts: false,
  }));
  const {
    result: personalResult,
    loading: personalLoading,
    error: personalError,
    refetch: refetchPersonal,
  } = useQuery(accountsQuery, personalQueryVariables, personalQueryOptions);

  const businessQueryOptions = computed(() => ({
    enabled: isUserLoggedIn.value && !isPersonalContext.value && businessId.value.length > 0,
    fetchPolicy: fetchPolicy.value,
    notifyOnNetworkStatusChange: notifyOnNetworkStatusChange.value,
  }));
  const businessQueryVariables = computed(() => ({ businessId: businessId.value }));
  const {
    result: businessResult,
    loading: businessLoading,
    error: businessError,
    refetch: refetchBusiness,
  } = useQuery(businessAccountsQuery, businessQueryVariables, businessQueryOptions);

  const accounts = computed(() => {
    const raw = isPersonalContext.value ? personalResult.value?.accounts : businessResult.value?.businessAccounts;
    return sortAccounts(raw?.map(a => useFragment(AccountFragmentFragmentDoc, a)) ?? undefined);
  });

  const loading = computed(() => (isPersonalContext.value ? personalLoading.value : businessLoading.value));
  const error = computed(() => (isPersonalContext.value ? personalError.value : businessError.value));
  const refetch = async () => {
    if (isPersonalContext.value) {
      return refetchPersonal();
    }
    return refetchBusiness();
  };

  const loadingFirstTime = computed(() => loading.value && isEmpty(accounts.value));

  const groupedAccounts = computed(() => {
    return group(accounts.value ?? [], a => a.institution?.name || "");
  });

  const cashAccounts = computed(() => accounts.value?.filter(account => !nonCashAccountTypes.includes(account.type)));
  const cashAccountsBalance = computed(() => (cashAccounts.value ? sum(cashAccounts.value, account => account.currentBalance) : 0));

  return { accounts, loading, loadingFirstTime, error, groupedAccounts, refetch, cashAccounts, cashAccountsBalance };
}
